import { makeEndpoint } from './Api3Endpoint';
import { Api3Account } from './types/Api3Account';
import { Api3AccountBasicProfiles } from './types/Api3AccountBasicProfiles';
import { Api3AccountPreferences } from './types/Api3AccountPreferences';
import { Api3Appointment } from './types/Api3Appointment';
import { Api3BasicProfile, Phone } from './types/Api3BasicProfile';
import { Api3Booking } from './types/Api3Booking';
import { ExternalId, HealthInterests } from './types/Api3Braze';
import { Api3CTA, Api3CTAKeys } from './types/Api3CTAs';
import { Api3Favourite } from './types/Api3Favourite';
import { Api3Favourites } from './types/Api3Favourites';
import { Api3PracticeProfile } from './types/Api3PracticeProfile';
import { Api3PrivacyPreferences, PrivacyPreferenceData } from './types/Api3PrivacyPreferences';
import { Api3Profile } from './types/Api3Profile';
import { Api3SharedData } from './types/Api3SharedData';

export const account = {
  addFavourite: (profileId: string, profileType: 'Practice' | 'Practitioner') => {
    const key = profileType === 'Practice' ? 'practice_id' : 'practitioner_id';
    return makeEndpoint<Api3Favourite>(`account/favourites`, {
      method: 'POST',
      body: JSON.stringify({ [key]: profileId }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
  deleteFavourite: (favouriteId: string) =>
    makeEndpoint(`account/favourites/${favouriteId}`, { method: 'DELETE' }),
  getAccount: () => makeEndpoint<Api3Account>(`account`),
  getAppointments: () => makeEndpoint<Api3Appointment[]>(`account/bookings/extended`),
  getPrimaryProfile: () => makeEndpoint<Api3BasicProfile>(`account/profile/main/basic`),
  getBasicProfiles: () => makeEndpoint<Api3AccountBasicProfiles>(`account/profiles/basic`),
  getCTAs: (keys: string[]) => makeEndpoint<Api3CTA>(`account/cta?key=${keys.join(',')}`),
  getBookings: (practiceId: string) =>
    makeEndpoint<Api3Booking[]>(`account/practice/${practiceId}/bookings`),
  getPracticeProfile: (practiceId: string) =>
    makeEndpoint<Api3PracticeProfile>(`practice/profile/${practiceId}`),
  getFavourites: () => makeEndpoint<Api3Favourites>(`account/favourites`),
  getFullPrimaryProfile: (otp?: string) =>
    makeEndpoint<Api3Profile>(`account/profile/main/full`, {
      headers: otp ? { verification: otp } : undefined,
    }),
  getPreferences: () => makeEndpoint<Api3AccountPreferences>(`account/preferences`),
  getPrivacyPreferences: () => makeEndpoint<Api3PrivacyPreferences>(`account/privacy-preferences`),
  getSharedData: () => makeEndpoint<Api3SharedData[]>(`account/shared-data`),
  saveCTAs: (keys: Api3CTAKeys) =>
    makeEndpoint<Api3CTA>(`account/cta`, {
      method: 'PATCH',
      body: JSON.stringify(keys),
    }),
  saveProfile: (profile: Api3Profile) =>
    makeEndpoint<Api3BasicProfile>(`account/profile/${profile.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(profile),
    }),
  savePrivacyPreferences: (privacyPreferences: PrivacyPreferenceData) =>
    makeEndpoint(`account/privacy-preferences`, {
      method: 'PATCH',
      body: JSON.stringify(privacyPreferences),
      headers: { 'content-type': 'application/json' },
    }),
  verifyMobile: (profileId: string) =>
    makeEndpoint<Phone>(`account/profile/${profileId}/verify/mobile`, {
      method: 'POST',
    }),
  verifyMobileValidate: (profileId: string, token: string) =>
    makeEndpoint(`account/profile/${profileId}/verify/mobile/validate`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
};

export const braze = {
  getExternalId: (email: string, phone: string) =>
    makeEndpoint<ExternalId>('braze/external-id', {
      method: 'POST',
      body: new URLSearchParams({ email, phone }),
    }),
  getUserHealthInterests: () =>
    makeEndpoint<HealthInterests>('account/health-interests-new', {
      method: 'GET',
    }),
  saveUserHealthInterests: (interests: { name: string; subscribed: boolean }[]) =>
    makeEndpoint('account/health-interests', {
      method: 'POST',
      // eslint-disable-next-line camelcase
      body: JSON.stringify({ health_interests: interests }),
      headers: { 'content-type': 'application/json' },
    }),
};
